/**
 * Creation Intent Registry（创建意图注册表）
 * ------------------------------------------------------------
 * 目标：
 * - 用“查表”的方式驱动 UI：一级 Tab 选什么 -> 显示对应二级 Tab 列表
 * - 后续你可以继续扩展：二级 -> 三级、校验规则、路由目标、回填映射等
 *
 * 这里先按你的要求：
 * - 只定义“一级 Tab”（图一）以及“一级选中后渲染二级 Tab”
 * - 二级 Tab 我先随便给一个（可扩展为多个）
 *
 * 使用方式（示例）：
 * - import { getMainCateroryTabs, getSpecificTabsBymainCategory } from '~/utils/creationIntentRegistry'
 * - const primaryTabs = getMainCateroryTabs()
 * - const secondaryTabs = getSpecificTabsBymainCategory(selectedPrimary)
 */

/**
 * 一级 Tab 的枚举（图一：Chat / 生成图片 / 生成视频 / ...）
 * 注意：这里用“稳定 id”作为代码值，显示文案交给 UI/i18n 去做。
 */
import type {
    CustomShotImageRefSnapshot,
    CustomShotItem,
} from '~/utils/customShot'
import { useAppStore } from '~/stores/app'

export type PrimaryTabId =
    | 'CHAT'
    | 'IMAGE_GENERATE'
    | 'VIDEO_GENERATE'
    | 'MUSIC_GENERATE'
    | 'IMAGE_EDIT'
    | 'VIDEO_EDIT'
    | 'IMAGE_TEMPLATE'
    | 'VIDEO_TEMPLATE'

/**
 * 二级 Tab 的枚举（示例：当一级=生成图片时，二级=文生图 / 图生图）
 * 你可以按业务逐步补齐。
 */
export type SecondaryTabId =
    | 'TEXT_TO_IMAGE'
    | 'IMAGE_TO_IMAGE'
    | 'TEXT_TO_VIDEO'
    | 'IMAGE_TO_VIDEO'
    | 'KEYFRAMES'
    | 'VOICE'
    | 'PURE_MUSIC'
    | 'CHAT_DEFAULT'
    | 'IMAGE_TEMPLATE_DEFAULT'
    | 'VIDEO_TEMPLATE_DEFAULT'
    | 'IMAGE_EDIT_DEFAULT'
    | 'VIDEO_EDIT_DEFAULT'
    | 'CHARACTER_TO_IMAGE'
    | 'CHARACTER_TO_VIDEO'
    | 'VIDEO_EDIT2'

/**
 * 三级 Tab 的枚举（示例：当二级=人声时，三级=男声/女声）
 * - 方案 B：三级不嵌在二级结构里，而是单独用 “二级 -> 三级” 的映射表查询。
 */
export type TertiaryTabId =
    | 'BOY_VOICE'
    | 'GIRL_VOICE'

/**
 * 并联配置（groups）里的“标签类”示例 id
 * - 你也可以改成 string 并由后端驱动
 */
export type VoiceTagId = 'SOFT' | 'DEEP' | 'SWEET' | 'POWERFUL'

export type RegistryIcon =
    | 'chat'
    | 'image'
    | 'video'
    | 'music'
    | 'image-edit'
    | 'video-edit'
    | 'image-template'
    | 'video-template'
    | 'tools'
    | 'text2image'
    | 'image2image'
    | 'text2video'
    | 'image2video'
    | 'keyframes'
    | 'voice'
    | 'sonic-wave'
    | 'pure-music'
    | 'character2image'
    | 'character2video'

export interface PrimaryTab {
    /** 稳定 id（用于逻辑/查表） */
    id: PrimaryTabId
    /** 展示用 label（这里先用中文占位；实际可走 i18n key） */
    label: string
    /** 仅用于 UI 展示的 icon key（可选） */
    icon?: RegistryIcon
}

export interface SecondaryTab {
    id: SecondaryTabId
    label: string
    icon?: RegistryIcon
}

export interface TertiaryTab {
    id: TertiaryTabId
    label: string
    icon?: RegistryIcon
}

export interface VoiceTagOption {
    id: VoiceTagId
    label: string
}

export const VOICE_GENDER_OPTIONS: ReadonlyArray<TertiaryTab> = Object.freeze([
    // 三级 icon 暂复用 voice；后续有专用资源再扩展 RegistryIcon + UI 映射
    { id: 'BOY_VOICE', label: 'workbench.tertiaryTabs.boyVoice', icon: 'sonic-wave' },
    { id: 'GIRL_VOICE', label: 'workbench.tertiaryTabs.girlVoice', icon: 'sonic-wave' },
])

/**
 * 二级下的“并联配置组”（方案 B 扩展）
 * - kind='select'：单选（例如：男女声）
 * - kind='multi-select'：多选（通用下拉多选）
 * - kind='music-tags'：音乐标签（专用 UI：曲风/情感/乐器），不从 options 渲染
 */
export type SecondaryConfigGroup =
    | {
        id: string
        label: string
        kind: 'select'
        options: ReadonlyArray<TertiaryTab>
        defaultId?: TertiaryTabId
    }
    | {
        id: string
        label: string
        kind: 'multi-select'
        options: ReadonlyArray<VoiceTagOption>
        defaultIds?: ReadonlyArray<VoiceTagId>
    }
    | {
        /** Usually `voice_tags` */
        id: string
        label: string
        kind: 'music-tags'
    }

export interface SecondaryConfig {
    groups: ReadonlyArray<SecondaryConfigGroup>
}

/**
 * Workbench: 二级类型 -> 后端模型分类映射
 *
 * 说明：
 * - 后端模型来自 `/api/v1/creative/models/all`，前端在 `appStore.modelData` 里缓存。
 * - `image_models` / `video_models` 下按 key 分组（如：text_to_image / image_to_video）。
 * - “首尾帧”(KEYFRAMES) 仍使用 `video_models.image_to_video` 的模型池，但必须过滤 `is_support_first_last_frame=true`。
 */
export type ImageModelTypeKey = 'text_to_image' | 'image_to_image'
export type VideoModelTypeKey = 'text_to_video' | 'image_to_video'

export type WorkbenchModelCategory =
    | { kind: 'image'; key: ImageModelTypeKey; requireFirstLastFrameSupport?: false }
    | { kind: 'video'; key: VideoModelTypeKey; requireFirstLastFrameSupport?: boolean }

export const WORKBENCH_MODEL_CATEGORY_BY_SECONDARY: Partial<
    Record<SecondaryTabId, WorkbenchModelCategory>
> = {
    TEXT_TO_IMAGE: { kind: 'image', key: 'text_to_image' },
    IMAGE_TO_IMAGE: { kind: 'image', key: 'image_to_image' },
    // Character-to-image reuses the image-to-image model pool
    CHARACTER_TO_IMAGE: { kind: 'image', key: 'image_to_image' },
    TEXT_TO_VIDEO: { kind: 'video', key: 'text_to_video' },
    IMAGE_TO_VIDEO: { kind: 'video', key: 'image_to_video' },
    // Character-to-video reuses the image-to-video model pool
    CHARACTER_TO_VIDEO: { kind: 'video', key: 'image_to_video' },
    KEYFRAMES: { kind: 'video', key: 'image_to_video', requireFirstLastFrameSupport: true },
} as const

export function getWorkbenchModelCategoryBySecondary(
    secondaryId: SecondaryTabId | null | undefined
): WorkbenchModelCategory | null {
    if (!secondaryId) return null
    return WORKBENCH_MODEL_CATEGORY_BY_SECONDARY[secondaryId] || null
}

/**
 * 注册表主体：一级 Tab 列表（决定 UI 上一级 tab 显示什么）
 * - 顺序就是 UI 的展示顺序
 */
export const PRIMARY_TABS: ReadonlyArray<PrimaryTab> = Object.freeze([
    { id: 'CHAT', label: 'workbench.primaryTabs.chat', icon: 'chat' },
    { id: 'IMAGE_GENERATE', label: 'workbench.primaryTabs.imageGenerate', icon: 'image' },
    { id: 'VIDEO_GENERATE', label: 'workbench.primaryTabs.videoGenerate', icon: 'video' },
    { id: 'MUSIC_GENERATE', label: 'workbench.primaryTabs.musicGenerate', icon: 'music' },
    { id: 'IMAGE_EDIT', label: 'workbench.primaryTabs.imageEdit', icon: 'image-edit' },
    { id: 'VIDEO_EDIT', label: 'workbench.primaryTabs.videoEdit', icon: 'video-edit' },
    { id: 'IMAGE_TEMPLATE', label: 'workbench.primaryTabs.imageTemplate', icon: 'image-template' },
    { id: 'VIDEO_TEMPLATE', label: 'workbench.primaryTabs.videoTemplate', icon: 'video-template' },
])

/**
 * 注册表主体：一级 -> 二级 的映射（决定“选中某个一级 tab 后，二级 tab 显示什么”）
 *
 * 你要测试可行性，所以这里每个一级至少给一个二级。
 * 后续扩展时：只需要往这里加二级配置，不需要改公共组件逻辑。
 */
/**
 * 关键点：这里用 `as const satisfies ...`，让 TS 保留字面量类型。
 * - 否则在 Object.freeze 下，`id` 很容易被推断为 string，导致类型报错。
 */
export const SECONDARY_TABS_BY_PRIMARY = {
    CHAT: [],

    IMAGE_GENERATE: [
        { id: 'TEXT_TO_IMAGE', label: 'workbench.secondaryTabs.textToImage', icon: 'text2image' },
        { id: 'IMAGE_TO_IMAGE', label: 'workbench.secondaryTabs.imageToImage', icon: 'image2image' },
        { id: 'CHARACTER_TO_IMAGE', label: 'workbench.secondaryTabs.characterToImage', icon: 'character2image' },
    ],

    VIDEO_GENERATE: [
        { id: 'TEXT_TO_VIDEO', label: 'workbench.secondaryTabs.textToVideo', icon: 'text2video' },
        { id: 'IMAGE_TO_VIDEO', label: 'workbench.secondaryTabs.imageToVideo', icon: 'image2video' },
        { id: 'KEYFRAMES', label: 'workbench.secondaryTabs.keyframes', icon: 'keyframes' },
        { id: 'CHARACTER_TO_VIDEO', label: 'workbench.secondaryTabs.characterToVideo', icon: 'character2video' },
        { id: 'VIDEO_EDIT2', label: 'workbench.secondaryTabs.videoEdit', icon: 'video-edit' },
    ],

    MUSIC_GENERATE: [
        { id: 'VOICE', label: 'workbench.secondaryTabs.voice', icon: 'voice' },
        { id: 'PURE_MUSIC', label: 'workbench.secondaryTabs.pureMusic', icon: 'pure-music' },
    ],

    IMAGE_EDIT: [],

    VIDEO_EDIT: [],

    IMAGE_TEMPLATE: [],

    VIDEO_TEMPLATE: [],
} as const satisfies Record<PrimaryTabId, ReadonlyArray<SecondaryTab>>

/**
 * 注册表主体：二级 -> 并联配置组（groups）
 * - 用于“同一个二级下，同时渲染多个配置项/选择器”
 */
export const SECONDARY_CONFIG_BY_ID: Partial<Record<SecondaryTabId, SecondaryConfig>> = {
    VOICE: {
        groups: [
            {
                id: 'voice_gender',
                label: 'workbench.configGroups.voiceGender',
                kind: 'select',
                options: VOICE_GENDER_OPTIONS,
            },
            {
                id: 'voice_tags',
                label: 'workbench.configGroups.tags',
                kind: 'music-tags',
            },
        ],
    },
    PURE_MUSIC: {
        groups: [
            {
                id: 'voice_tags',
                label: 'workbench.configGroups.tags',
                kind: 'music-tags',
            },
        ],
    },
} as const

type I18nT = (key: string, params?: any) => string
const translateMaybe = (t: I18nT | undefined, keyOrText: string) =>
    t ? t(keyOrText) : keyOrText

const HIDDEN_CHARACTER_SECONDARY_IDS = new Set<SecondaryTabId>([
    'CHARACTER_TO_IMAGE',
    'CHARACTER_TO_VIDEO',
])

function filterCharacterSecondaryTabs(
    tabs: ReadonlyArray<SecondaryTab>
): SecondaryTab[] {
    const appStore = useAppStore()
    if (!appStore.hideCharacterEntries) {
        return [...tabs]
    }
    return tabs.filter(
        (tab): tab is SecondaryTab =>
            !HIDDEN_CHARACTER_SECONDARY_IDS.has(tab.id)
    )
}

/**
 * 给 UI 用的 helper：拿一级 tabs
 */
export function getMainCateroryTabs(t?: I18nT): ReadonlyArray<PrimaryTab> {
    return PRIMARY_TABS.map((tab) => ({
        ...tab,
        label: translateMaybe(t, tab.label),
    }))
}

/**
 * 给 UI 用的 helper：根据一级 tab id 拿二级 tabs
 * - 如果没传 primaryId，则返回空数组（UI 就不渲染二级）
 */
export function getSpecificTabsBymainCategory(
    primaryId: PrimaryTabId | null | undefined,
    t?: I18nT
): ReadonlyArray<SecondaryTab> {
    if (!primaryId) return []
    const tabs = filterCharacterSecondaryTabs(
        SECONDARY_TABS_BY_PRIMARY[primaryId] || []
    )
    return tabs.map((tab) => ({
        ...tab,
        label: translateMaybe(t, tab.label),
    }))
}


// ─── PC 工作台顶部横排 Display Tab（将 4 个编辑/模板合并为 TOOLS） ───

export type DisplayTabId = 'CHAT' | 'IMAGE_GENERATE' | 'VIDEO_GENERATE' | 'MUSIC_GENERATE' | 'TOOLS'
export type ImageEditDisplayTabSource = 'IMAGE_GENERATE' | 'TOOLS'

export interface DisplayTab {
    id: DisplayTabId
    label: string
    icon?: RegistryIcon
    hasDropdown: boolean
}

const TOOLS_PRIMARY_IDS: ReadonlyArray<PrimaryTabId> = [
    'IMAGE_EDIT', 'VIDEO_EDIT', 'IMAGE_TEMPLATE', 'VIDEO_TEMPLATE',
] as const

export function isToolsPrimaryId(id: PrimaryTabId | null | undefined): boolean {
    return !!id && (TOOLS_PRIMARY_IDS as readonly string[]).includes(id)
}

export interface DisplayDropdownItem {
    label: string
    icon?: RegistryIcon
    primaryId: PrimaryTabId
    secondaryId?: SecondaryTabId
    displayTabId?: DisplayTabId
}

const DISPLAY_TABS: ReadonlyArray<DisplayTab> = Object.freeze([
    { id: 'CHAT', label: 'workbench.primaryTabs.chat', icon: 'chat', hasDropdown: false },
    { id: 'IMAGE_GENERATE', label: 'workbench.primaryTabs.imageGenerate', icon: 'image', hasDropdown: true },
    { id: 'VIDEO_GENERATE', label: 'workbench.primaryTabs.videoGenerate', icon: 'video', hasDropdown: true },
    { id: 'MUSIC_GENERATE', label: 'workbench.primaryTabs.musicGenerate', icon: 'music', hasDropdown: true },
    { id: 'TOOLS', label: 'workbench.primaryTabs.tools', icon: 'tools', hasDropdown: true },
])

export function getDisplayTabs(t?: I18nT): ReadonlyArray<DisplayTab> {
    return DISPLAY_TABS.map((tab) => ({
        ...tab,
        label: translateMaybe(t, tab.label),
    }))
}

const TOOLS_DROPDOWN_ITEMS: ReadonlyArray<{ label: string; icon?: RegistryIcon; primaryId: PrimaryTabId }> = [
    { label: 'workbench.primaryTabs.imageEdit', icon: 'image-edit', primaryId: 'IMAGE_EDIT' },
    { label: 'workbench.primaryTabs.videoEdit', icon: 'video-edit', primaryId: 'VIDEO_EDIT' },
    { label: 'workbench.primaryTabs.imageTemplate', icon: 'image-template', primaryId: 'IMAGE_TEMPLATE' },
    { label: 'workbench.primaryTabs.videoTemplate', icon: 'video-template', primaryId: 'VIDEO_TEMPLATE' },
]

export function getDropdownItemsForDisplayTab(
    displayId: DisplayTabId,
    t?: I18nT
): ReadonlyArray<DisplayDropdownItem> {
    if (displayId === 'TOOLS') {
        return TOOLS_DROPDOWN_ITEMS.map((item) => ({
            ...item,
            label: translateMaybe(t, item.label),
            displayTabId: 'TOOLS',
        }))
    }
    const primaryId = displayId as PrimaryTabId
    const secondaryTabs = filterCharacterSecondaryTabs(
        SECONDARY_TABS_BY_PRIMARY[primaryId] || []
    )
    const items: DisplayDropdownItem[] = secondaryTabs.map((tab) => ({
        label: translateMaybe(t, tab.label),
        icon: tab.icon,
        primaryId,
        secondaryId: tab.id,
        displayTabId: displayId,
    }))
    if (displayId === 'IMAGE_GENERATE') {
        items.push({
            label: translateMaybe(t, 'workbench.primaryTabs.imageEdit'),
            icon: 'image-edit',
            primaryId: 'IMAGE_EDIT',
            displayTabId: 'IMAGE_GENERATE',
        })
    }
    return items
}

export function displayTabIdFromPrimary(
    primaryId: PrimaryTabId | null | undefined,
    imageEditDisplayTabSource?: ImageEditDisplayTabSource | null
): DisplayTabId {
    if (!primaryId) return 'CHAT'
    if (primaryId === 'IMAGE_EDIT' && imageEditDisplayTabSource === 'IMAGE_GENERATE') {
        return 'IMAGE_GENERATE'
    }
    if (isToolsPrimaryId(primaryId)) return 'TOOLS'
    return primaryId as DisplayTabId
}


/**
 * 给 UI 用的 helper：根据二级 tab id 拿“并联配置组”（方案 B 扩展）
 */
export function getSMultipleConfigBySecondary(
    secondaryId: SecondaryTabId | null | undefined,
    t?: I18nT
): SecondaryConfig | null {
    if (!secondaryId) return null
    const cfg = SECONDARY_CONFIG_BY_ID[secondaryId]
    if (!cfg) return null

    return {
        groups: cfg.groups.map((g) => {
            if (g.kind === 'select') {
                return {
                    ...g,
                    label: translateMaybe(t, g.label),
                    options: g.options.map((opt) => ({
                        ...opt,
                        label: translateMaybe(t, opt.label),
                    })),
                }
            }
            return {
                ...g,
                label: translateMaybe(t, g.label),
            }
        }),
    }
}

/**
 * Workbench: SecondaryTabId -> route target + prefill mapping
 * ------------------------------------------------------------
 * 目标：
 * - PcWorkbench 只负责收集“通用选择结果”（payload），不关心每个工具页怎么命名字段
 * - 工具页（如 text-to-image）只消费“标准化 prefill”，不关心工作台内部字段结构
 *
 * 最小 PoC：先只实现 TEXT_TO_IMAGE，其他二级按同样方式逐步补齐。
 */
export type WorkbenchRouteTarget = {
    /** Nuxt page route path (no locale prefix) */
    path: string
    /** handoff storage key */
    handoffKey: string
}

export type WorkbenchTextToImagePrefill = {
    prompt?: string
    model?: string
    aspect_ratio?: string
    output_number?: number
    /**
     * Resolution selection coming from Workbench.
     * Mapped to `image_size` on `pages/text-to-image`.
     */
    resolution?: string
    quality?: string
    use_web_search?: boolean
    public_visible?: boolean
    copyright_protection?: boolean
    /**
     * Workbench generate mode. Current phase: always `standard`.
     * Keep it for forward-compat (batch compare).
     */
    generate_type?: 'standard' | 'batch'
    /**
     * Batch compare models. Current phase: omitted or empty array.
     */
    model_list?: string[]
}

export type WorkbenchImageToImagePrefill = {
    prompt?: string
    model?: string
    aspect_ratio?: string
    output_number?: number
    resolution?: string
    quality?: string
    use_web_search?: boolean
    public_visible?: boolean
    copyright_protection?: boolean
    generate_type?: 'standard' | 'batch' | 'single'
    model_list?: string[]
    upload_images?: string[]
}

export type WorkbenchCharacterToImagePrefill = {
    prompt?: string
    model?: string
    aspect_ratio?: string
    output_number?: number
    resolution?: string
    quality?: string
    use_web_search?: boolean
    public_visible?: boolean
    copyright_protection?: boolean
    generate_type?: 'standard' | 'batch' | 'single'
    model_list?: string[]
    /** Uploaded images selected on Workbench Character-to-Image panel */
    upload_images?: string[]
    /** Selected character objects (kept as-is; consumer may normalize) */
    character_select_list?: any[]
}

export type WorkbenchTextToVideoPrefill = {
    prompt?: string
    intelligent_prompt?: string
    custom_shot_enabled?: boolean
    custom_shot_mode?: boolean
    multi_prompt?: CustomShotItem[]
    shot_reference_images?: string[]
    shot_reference_image_ref_snapshots?: CustomShotImageRefSnapshot[]
    active_shot_index?: number
    model?: string
    /**
     * Optional model version key (when Workbench selects a version item).
     * Tool pages bind this to `model_version`.
     */
    model_version?: string
    /**
     * Optional parent model key (category.code) for grouped models.
     * Tool pages bind this to `edit_model`.
     */
    selectedModelParentKey?: string
    aspect_ratio?: string
    output_number?: number
    duration?: number
    resolution?: string
    generate_audio?: boolean
    public_visible?: boolean
    copyright_protection?: boolean
    negative_prompt?: string
}

export type WorkbenchImageToVideoPrefill = {
    prompt?: string
    intelligent_prompt?: string
    custom_shot_enabled?: boolean
    custom_shot_mode?: boolean
    multi_prompt?: CustomShotItem[]
    shot_reference_images?: string[]
    shot_reference_image_ref_snapshots?: CustomShotImageRefSnapshot[]
    active_shot_index?: number
    model?: string
    /**
     * Optional model version key (when Workbench selects a version item).
     * Tool pages bind this to `model_version`.
     */
    model_version?: string
    /**
     * Optional parent model key (category.code) for grouped models.
     * Tool pages bind this to `edit_model`.
     */
    selectedModelParentKey?: string
    aspect_ratio?: string
    output_number?: number
    duration?: number
    resolution?: string
    generate_audio?: boolean
    public_visible?: boolean
    copyright_protection?: boolean
    negative_prompt?: string
    almighty_mode?: boolean
    almighty_images?: string[]
    almighty_videos?: string[]
    almighty_audios?: string[]
    almighty_reference_items?: Array<{
        id?: string
        kind?: 'image' | 'video' | 'audio'
        url?: string
        name?: string
        duration?: number
        loading?: boolean
    }>
    almighty_prompt_tokens?: Array<
        | {
            type: 'text'
            text?: string
        }
        | {
            type: 'mention'
            assetId?: string
            kind?: 'image' | 'video' | 'audio'
            fallbackLabel?: string
        }
    >
    upload_images?: string[]
    image_type?: 'single_frame' | 'multiple_frames'
}

/**
 * Workbench -> Character-to-video page prefill
 * - Align with `pages/character-to-video/index.vue` state fields and
 *   `components/Tool/CharacterToVidSetting.vue` v-model bindings.
 * - For first/last frame character selection: tool-side uses a single array:
 *   `character_select_list[0]` = first frame character, `[1]` = last frame character.
 */
export type WorkbenchCharacterToVideoPrefill = {
    prompt?: string
    /**
     * Parent model key (category.code) for grouped models.
     * Tool page binds this to `edit_model`.
     */
    model?: string
    /** Optional version model key (tool page binds to `model_version`). */
    model_version?: string
    /** Optional parent model key (kept for compatibility with IMAGE_TO_VIDEO handler). */
    selectedModelParentKey?: string

    aspect_ratio?: string
    output_number?: number
    duration?: number
    resolution?: string
    generate_audio?: boolean
    public_visible?: boolean
    copyright_protection?: boolean
    negative_prompt?: string

    /** image mode */
    image_type?: 'single_frame' | 'multiple_frames'
    frame_end_mode?: boolean

    /** single frame */
    image_url?: string

    /** first/last frame */
    first_image_url?: string
    last_image_url?: string

    /** multiple frames */
    image_list?: string[]

    /** Tool page expects raw objects (kept as-is; consumer may normalize). */
    character_select_list?: any[]
}

export type WorkbenchTextToMusicPrefill = {
    /**
     * vocal: 人声歌曲
     * inst: 纯音乐
     */
    mode?: 'vocal' | 'inst'
    /**
     * Tool page uses `description` as the main prompt field submitted to API.
     * Workbench uses `music_inputText` as user-entered theme text; map it here.
     */
    description?: string
    /**
     * Pure user input text used for canSubmit validation on the page.
     */
    inputText?: string
    /**
     * Music tags: key enum value (e.g. POP), value localized label.
     */
    styles?: Record<string, string>
    lyrics?: string
    referenceAudioUrl?: string
    referenceAudioName?: string
    gender?: 'm' | 'f'
    public_visible?: boolean
    copyright_protection?: boolean
}

/**
 * Workbench -> video-to-video (video editor) prefill
 * - Minimal fields: input video url + prompt
 * - Intentionally does NOT include quick switch preference fields.
 */
export type WorkbenchVideoEditPrefill = {
    prompt?: string
    video_url?: string
    video_duration?: number
}

export type WorkbenchPrefillBySecondary = {
    TEXT_TO_IMAGE: WorkbenchTextToImagePrefill
    IMAGE_TO_IMAGE: WorkbenchImageToImagePrefill
    CHARACTER_TO_IMAGE: WorkbenchCharacterToImagePrefill
    TEXT_TO_VIDEO: WorkbenchTextToVideoPrefill
    IMAGE_TO_VIDEO: WorkbenchImageToVideoPrefill
    KEYFRAMES: WorkbenchImageToVideoPrefill
    CHARACTER_TO_VIDEO: WorkbenchCharacterToVideoPrefill
    VOICE: WorkbenchTextToMusicPrefill
    PURE_MUSIC: WorkbenchTextToMusicPrefill
    VIDEO_EDIT2: WorkbenchVideoEditPrefill
}

export const WORKBENCH_ROUTE_BY_SECONDARY: Partial<
    Record<SecondaryTabId, WorkbenchRouteTarget>
> = {
    TEXT_TO_IMAGE: { path: '/text-to-image', handoffKey: 'wery_workbench_handoff' },
    IMAGE_TO_IMAGE: {
        path: '/image-to-image',
        handoffKey: 'wery_workbench_handoff_image_to_image',
    },
    CHARACTER_TO_IMAGE: {
        path: '/character-to-image',
        handoffKey: 'wery_workbench_handoff_character_to_image',
    },
    TEXT_TO_VIDEO: {
        path: '/text-to-video',
        handoffKey: 'wery_workbench_handoff_text_to_video',
    },
    IMAGE_TO_VIDEO: {
        path: '/image-to-video',
        handoffKey: 'wery_workbench_handoff_image_to_video',
    },
    // KEYFRAMES reuses image-to-video page + same handoffKey
    KEYFRAMES: {
        path: '/image-to-video',
        handoffKey: 'wery_workbench_handoff_image_to_video',
    },
    CHARACTER_TO_VIDEO: {
        path: '/character-to-video',
        handoffKey: 'wery_workbench_handoff_character_to_video',
    },
    // Music (VOICE / PURE_MUSIC) shares the same tool page
    VOICE: {
        path: '/text-to-music',
        handoffKey: 'wery_workbench_handoff_text_to_music',
    },
    PURE_MUSIC: {
        path: '/text-to-music',
        handoffKey: 'wery_workbench_handoff_text_to_music',
    },
    VIDEO_EDIT2: {
        path: '/video-to-video',
        handoffKey: 'wery_workbench_handoff_video_edit2',
    },
} as const

/**
 * 将 PcWorkbench 的“通用 payload”映射为各工具页可消费的 prefill。
 * - 这里的 payload 类型暂用最小约束（避免 Workbench 内部类型外泄/强耦合）
 */
function buildWorkbenchImageToVideoPrefillFromPayload(payload: any): WorkbenchImageToVideoPrefill {
    const out: WorkbenchImageToVideoPrefill = {}
    if (typeof payload?.positive_prompts === 'string') {
        out.prompt = payload.positive_prompts
    }
    if (typeof payload?.intelligent_prompt === 'string') {
        out.intelligent_prompt = payload.intelligent_prompt
    }
    if (typeof payload?.custom_shot_enabled === 'boolean') {
        out.custom_shot_enabled = payload.custom_shot_enabled
    }
    if (typeof payload?.custom_shot_mode === 'boolean') {
        out.custom_shot_mode = payload.custom_shot_mode
    }
    if (Array.isArray(payload?.multi_prompt)) {
        out.multi_prompt = payload.multi_prompt.map((item: any) => ({
            prompt: typeof item?.prompt === 'string' ? item.prompt : '',
            duration:
                typeof item?.duration === 'number'
                    ? item.duration
                    : Number(item?.duration || 1) || 1,
        }))
    }
    if (Array.isArray(payload?.shot_reference_images)) {
        out.shot_reference_images = payload.shot_reference_images
            .filter((it: any): it is string => typeof it === 'string' && !!it)
            .slice(0, 10)
    }
    if (Array.isArray(payload?.shot_reference_image_ref_snapshots)) {
        out.shot_reference_image_ref_snapshots = payload.shot_reference_image_ref_snapshots
            .map((item: any) => ({
                url: typeof item?.url === 'string' ? item.url : '',
                stableIndex:
                    typeof item?.stableIndex === 'number'
                        ? item.stableIndex
                        : Number(item?.stableIndex || 0) || undefined,
                displayIndex:
                    typeof item?.displayIndex === 'number'
                        ? item.displayIndex
                        : Number(item?.displayIndex || 0) || undefined,
            }))
            .filter((item: any) => typeof item.url === 'string' && !!item.url)
            .slice(0, 10)
    }
    if (typeof payload?.active_shot_index === 'number') {
        out.active_shot_index = Math.max(0, Math.floor(payload.active_shot_index))
    }
    // Workbench may select a "version item" (flat model_key) while tool pages expect:
    // - parent model key (category.code) in `model`
    // - version key in `model_version`
    const parentKey =
        typeof payload?.selectedModelParentKey === 'string'
            ? payload.selectedModelParentKey
            : ''
    const versionKey =
        typeof payload?.model_version === 'string' ? payload.model_version : ''
    const selectedKey =
        typeof payload?.selectedModelKey === 'string' ? payload.selectedModelKey : ''
    if (parentKey) {
        out.selectedModelParentKey = parentKey
        out.model = parentKey
        if (versionKey) out.model_version = versionKey
        else if (selectedKey) out.model_version = selectedKey
    } else if (selectedKey) {
        out.model = selectedKey
    }
    if (typeof payload?.selectedAspectRatio === 'string' && payload.selectedAspectRatio) {
        out.aspect_ratio = payload.selectedAspectRatio
    }
    if (typeof payload?.selectedResolution === 'string' && payload.selectedResolution) {
        out.resolution = payload.selectedResolution
    }
    const rawDuration = payload?.selectedDuration
    if (typeof rawDuration === 'number' && Number.isFinite(rawDuration) && rawDuration > 0) {
        out.duration = rawDuration
    }
    const rawCount = payload?.selectedOutputCount
    const n =
        typeof rawCount === 'number'
            ? rawCount
            : typeof rawCount === 'string'
                ? Number(rawCount)
                : NaN
    if (Number.isFinite(n) && n > 0) {
        out.output_number = n
    }
    if (typeof payload?.generate_audio === 'boolean') {
        out.generate_audio = payload.generate_audio
    }
    if (typeof payload?.public_visible === 'boolean') {
        out.public_visible = payload.public_visible
    }
    if (typeof payload?.copyright_protection === 'boolean') {
        out.copyright_protection = payload.copyright_protection
    }
    if (typeof payload?.almighty_mode === 'boolean') {
        out.almighty_mode = payload.almighty_mode
    }
    if (Array.isArray(payload?.almighty_images)) {
        out.almighty_images = payload.almighty_images
            .filter((it: any): it is string => typeof it === 'string' && !!it)
    }
    if (Array.isArray(payload?.almighty_videos)) {
        out.almighty_videos = payload.almighty_videos
            .filter((it: any): it is string => typeof it === 'string' && !!it)
    }
    if (Array.isArray(payload?.almighty_audios)) {
        out.almighty_audios = payload.almighty_audios
            .filter((it: any): it is string => typeof it === 'string' && !!it)
    }
    if (Array.isArray(payload?.almighty_reference_items)) {
        out.almighty_reference_items = payload.almighty_reference_items
            .filter((item: any) =>
                item &&
                ['image', 'video', 'audio'].includes(item.kind) &&
                typeof item.url === 'string' &&
                !!item.url
            )
            .map((item: any, index: number) => ({
                id:
                    typeof item.id === 'string' && item.id
                        ? item.id
                        : `almighty-prefill-${index}-${Date.now()}`,
                kind: item.kind,
                url: item.url,
                name: typeof item.name === 'string' ? item.name : undefined,
                duration: typeof item.duration === 'number' ? item.duration : undefined,
                loading: Boolean(item.loading),
            }))
    }
    if (Array.isArray(payload?.almighty_prompt_tokens)) {
        out.almighty_prompt_tokens = payload.almighty_prompt_tokens
            .filter((token: any) =>
                token &&
                (
                    (token.type === 'text' && typeof token.text === 'string') ||
                    (
                        token.type === 'mention' &&
                        typeof token.assetId === 'string' &&
                        ['image', 'video', 'audio'].includes(token.kind) &&
                        typeof token.fallbackLabel === 'string'
                    )
                )
            )
            .map((token: any) =>
                token.type === 'text'
                    ? {
                        type: 'text' as const,
                        text: token.text,
                    }
                    : {
                        type: 'mention' as const,
                        assetId: token.assetId,
                        kind: token.kind,
                        fallbackLabel: token.fallbackLabel,
                    }
            )
    }
    if (typeof payload?.negative_prompt === 'string' && payload.negative_prompt.trim()) {
        out.negative_prompt = payload.negative_prompt
    }
    const hasAlmightyPayload =
        out.almighty_mode === true ||
        !!out.almighty_reference_items?.length ||
        !!out.almighty_images?.length ||
        !!out.almighty_videos?.length ||
        !!out.almighty_audios?.length
    if (hasAlmightyPayload) {
        out.image_type = 'multiple_frames'
    }
    if (Array.isArray(payload?.upload_images)) {
        const imgs = payload.upload_images.filter((it: any) => typeof it === 'string' && it)
        out.upload_images = imgs
        if (!hasAlmightyPayload) {
            out.image_type = imgs.length > 1 ? 'multiple_frames' : 'single_frame'
        }
    }
    return out
}

export function buildWorkbenchPrefillBySecondary(
    secondaryId: SecondaryTabId | null | undefined,
    payload: any
):
    | WorkbenchTextToImagePrefill
    | WorkbenchImageToImagePrefill
    | WorkbenchCharacterToImagePrefill
    | WorkbenchTextToVideoPrefill
    | WorkbenchImageToVideoPrefill
    | WorkbenchCharacterToVideoPrefill
    | WorkbenchTextToMusicPrefill
    | WorkbenchVideoEditPrefill
    | null {
    if (!secondaryId) return null
    if (secondaryId === 'TEXT_TO_IMAGE') {
        const out: WorkbenchTextToImagePrefill = {}
        if (typeof payload?.positive_prompts === 'string') {
            out.prompt = payload.positive_prompts
        }
        if (typeof payload?.selectedModelKey === 'string' && payload.selectedModelKey) {
            out.model = payload.selectedModelKey
        }
        if (typeof payload?.selectedAspectRatio === 'string' && payload.selectedAspectRatio) {
            out.aspect_ratio = payload.selectedAspectRatio
        }
        if (typeof payload?.selectedResolution === 'string' && payload.selectedResolution) {
            out.resolution = payload.selectedResolution
        }
        if (typeof payload?.quality === 'string' && payload.quality) {
            out.quality = payload.quality
        }
        if (typeof payload?.use_web_search === 'boolean') {
            out.use_web_search = payload.use_web_search
        }
        if (typeof payload?.public_visible === 'boolean') {
            out.public_visible = payload.public_visible
        }
        if (typeof payload?.copyright_protection === 'boolean') {
            out.copyright_protection = payload.copyright_protection
        }
        // Workbench generate mode: standard / batch
        out.generate_type = payload?.generate_type === 'batch' ? 'batch' : 'standard'
        // Batch compare models (only when batch)
        out.model_list =
            out.generate_type === 'batch' && Array.isArray(payload?.model_list)
                ? payload.model_list
                    .filter((it: any) => typeof it === 'string' && it)
                : []
        const rawCount = payload?.selectedOutputCount
        const n =
            typeof rawCount === 'number'
                ? rawCount
                : typeof rawCount === 'string'
                    ? Number(rawCount)
                    : NaN
        if (Number.isFinite(n) && n > 0) {
            out.output_number = n
        }
        return out
    }
    if (secondaryId === 'VIDEO_EDIT2') {
        const out: WorkbenchVideoEditPrefill = {}
        if (typeof payload?.positive_prompts === 'string') {
            out.prompt = payload.positive_prompts
        }
        // Workbench VIDEO_EDIT2 uses a dedicated `video_url` field in payload.
        if (typeof payload?.video_url === 'string' && payload.video_url) {
            out.video_url = payload.video_url
        }
        if (
            typeof payload?.video_duration === 'number' &&
            Number.isFinite(payload.video_duration) &&
            payload.video_duration > 0
        ) {
            out.video_duration = payload.video_duration
        }
        return out
    }
    if (secondaryId === 'IMAGE_TO_IMAGE') {
        const out: WorkbenchImageToImagePrefill = {}
        if (typeof payload?.positive_prompts === 'string') {
            out.prompt = payload.positive_prompts
        }
        if (typeof payload?.selectedModelKey === 'string' && payload.selectedModelKey) {
            out.model = payload.selectedModelKey
        }
        if (typeof payload?.selectedAspectRatio === 'string' && payload.selectedAspectRatio) {
            out.aspect_ratio = payload.selectedAspectRatio
        }
        if (typeof payload?.selectedResolution === 'string' && payload.selectedResolution) {
            out.resolution = payload.selectedResolution
        }
        if (typeof payload?.quality === 'string' && payload.quality) {
            out.quality = payload.quality
        }
        if (typeof payload?.use_web_search === 'boolean') {
            out.use_web_search = payload.use_web_search
        }
        if (typeof payload?.public_visible === 'boolean') {
            out.public_visible = payload.public_visible
        }
        if (typeof payload?.copyright_protection === 'boolean') {
            out.copyright_protection = payload.copyright_protection
        }
        // Workbench generate mode: standard / batch / single
        out.generate_type =
            payload?.generate_type === 'batch'
                ? 'batch'
                : payload?.generate_type === 'single'
                    ? 'single'
                    : 'standard'
        out.model_list =
            out.generate_type === 'batch' && Array.isArray(payload?.model_list)
                ? payload.model_list
                    .filter((it: any) => typeof it === 'string' && it)
                : []
        if (Array.isArray(payload?.upload_images)) {
            out.upload_images = payload.upload_images.filter((it: any) => typeof it === 'string' && it)
        }
        const rawCount = payload?.selectedOutputCount
        const n =
            typeof rawCount === 'number'
                ? rawCount
                : typeof rawCount === 'string'
                    ? Number(rawCount)
                    : NaN
        if (Number.isFinite(n) && n > 0) {
            out.output_number = n
        }
        return out
    }
    if (secondaryId === 'CHARACTER_TO_IMAGE') {
        const out: WorkbenchCharacterToImagePrefill = {}
        if (typeof payload?.positive_prompts === 'string') {
            out.prompt = payload.positive_prompts
        }
        // Character-to-image uses the same model pool as image-to-image
        if (typeof payload?.selectedModelKey === 'string' && payload.selectedModelKey) {
            out.model = payload.selectedModelKey
        }
        if (typeof payload?.selectedAspectRatio === 'string' && payload.selectedAspectRatio) {
            out.aspect_ratio = payload.selectedAspectRatio
        }
        if (typeof payload?.selectedResolution === 'string' && payload.selectedResolution) {
            out.resolution = payload.selectedResolution
        }
        if (typeof payload?.quality === 'string' && payload.quality) {
            out.quality = payload.quality
        }
        if (typeof payload?.use_web_search === 'boolean') {
            out.use_web_search = payload.use_web_search
        }
        if (typeof payload?.public_visible === 'boolean') {
            out.public_visible = payload.public_visible
        }
        if (typeof payload?.copyright_protection === 'boolean') {
            out.copyright_protection = payload.copyright_protection
        }
        // Workbench generate mode: standard / batch / single
        out.generate_type =
            payload?.generate_type === 'batch'
                ? 'batch'
                : payload?.generate_type === 'single'
                    ? 'single'
                    : 'standard'
        out.model_list =
            out.generate_type === 'batch' && Array.isArray(payload?.model_list)
                ? payload.model_list.filter((it: any) => typeof it === 'string' && it)
                : []

        // Character-to-image has its own input panel; prefer those fields
        if (Array.isArray(payload?.character_edit_image_list)) {
            out.upload_images = payload.character_edit_image_list.filter(
                (it: any) => typeof it === 'string' && it
            )
        } else if (Array.isArray(payload?.upload_images)) {
            // fallback (should rarely be used)
            out.upload_images = payload.upload_images.filter((it: any) => typeof it === 'string' && it)
        }
        if (Array.isArray(payload?.character_select_list)) {
            out.character_select_list = payload.character_select_list
        }

        const rawCount = payload?.selectedOutputCount
        const n =
            typeof rawCount === 'number'
                ? rawCount
                : typeof rawCount === 'string'
                    ? Number(rawCount)
                    : NaN
        if (Number.isFinite(n) && n > 0) {
            out.output_number = n
        }
        return out
    }
    if (secondaryId === 'TEXT_TO_VIDEO') {
        const out: WorkbenchTextToVideoPrefill = {}
        if (typeof payload?.positive_prompts === 'string') {
            out.prompt = payload.positive_prompts
        }
        if (typeof payload?.intelligent_prompt === 'string') {
            out.intelligent_prompt = payload.intelligent_prompt
        }
        if (typeof payload?.custom_shot_enabled === 'boolean') {
            out.custom_shot_enabled = payload.custom_shot_enabled
        }
        if (typeof payload?.custom_shot_mode === 'boolean') {
            out.custom_shot_mode = payload.custom_shot_mode
        }
        if (Array.isArray(payload?.multi_prompt)) {
            out.multi_prompt = payload.multi_prompt.map((item: any) => ({
                prompt: typeof item?.prompt === 'string' ? item.prompt : '',
                duration:
                    typeof item?.duration === 'number'
                        ? item.duration
                        : Number(item?.duration || 1) || 1,
            }))
        }
        if (Array.isArray(payload?.shot_reference_images)) {
            out.shot_reference_images = payload.shot_reference_images
                .filter((it: any): it is string => typeof it === 'string' && !!it)
                .slice(0, 10)
        }
        if (Array.isArray(payload?.shot_reference_image_ref_snapshots)) {
            out.shot_reference_image_ref_snapshots = payload.shot_reference_image_ref_snapshots
                .map((item: any) => ({
                    url: typeof item?.url === 'string' ? item.url : '',
                    stableIndex:
                        typeof item?.stableIndex === 'number'
                            ? item.stableIndex
                            : Number(item?.stableIndex || 0) || undefined,
                    displayIndex:
                        typeof item?.displayIndex === 'number'
                            ? item.displayIndex
                            : Number(item?.displayIndex || 0) || undefined,
                }))
                .filter((item: any) => typeof item.url === 'string' && !!item.url)
                .slice(0, 10)
        }
        if (typeof payload?.active_shot_index === 'number') {
            out.active_shot_index = Math.max(0, Math.floor(payload.active_shot_index))
        }
        // Same model parent/version mapping as IMAGE_TO_VIDEO
        const parentKey =
            typeof payload?.selectedModelParentKey === 'string'
                ? payload.selectedModelParentKey
                : ''
        const versionKey =
            typeof payload?.model_version === 'string' ? payload.model_version : ''
        const selectedKey =
            typeof payload?.selectedModelKey === 'string'
                ? payload.selectedModelKey
                : ''
        if (parentKey) {
            out.selectedModelParentKey = parentKey
            out.model = parentKey
            if (versionKey) out.model_version = versionKey
            else if (selectedKey) out.model_version = selectedKey
        } else if (selectedKey) {
            out.model = selectedKey
        }
        if (typeof payload?.selectedAspectRatio === 'string' && payload.selectedAspectRatio) {
            out.aspect_ratio = payload.selectedAspectRatio
        }
        if (typeof payload?.selectedResolution === 'string' && payload.selectedResolution) {
            out.resolution = payload.selectedResolution
        }
        const rawDuration = payload?.selectedDuration
        if (typeof rawDuration === 'number' && Number.isFinite(rawDuration) && rawDuration > 0) {
            out.duration = rawDuration
        }
        const rawCount = payload?.selectedOutputCount
        const n =
            typeof rawCount === 'number'
                ? rawCount
                : typeof rawCount === 'string'
                    ? Number(rawCount)
                    : NaN
        if (Number.isFinite(n) && n > 0) {
            out.output_number = n
        }
        if (typeof payload?.generate_audio === 'boolean') {
            out.generate_audio = payload.generate_audio
        }
        if (typeof payload?.public_visible === 'boolean') {
            out.public_visible = payload.public_visible
        }
        if (typeof payload?.copyright_protection === 'boolean') {
            out.copyright_protection = payload.copyright_protection
        }
        // Only pass negative_prompt when it exists (and non-empty).
        if (typeof payload?.negative_prompt === 'string' && payload.negative_prompt.trim()) {
            out.negative_prompt = payload.negative_prompt
        }
        return out
    }
    if (secondaryId === 'CHARACTER_TO_VIDEO') {
        const out: WorkbenchCharacterToVideoPrefill = {}

        if (typeof payload?.positive_prompts === 'string') {
            out.prompt = payload.positive_prompts
        }

        // Align model parent/version mapping with IMAGE_TO_VIDEO
        const parentKey =
            typeof payload?.selectedModelParentKey === 'string'
                ? payload.selectedModelParentKey
                : ''
        const versionKey = typeof payload?.model_version === 'string' ? payload.model_version : ''
        const selectedKey =
            typeof payload?.selectedModelKey === 'string' ? payload.selectedModelKey : ''
        if (parentKey) {
            out.selectedModelParentKey = parentKey
            out.model = parentKey
            if (versionKey) out.model_version = versionKey
            else if (selectedKey) out.model_version = selectedKey
        } else if (selectedKey) {
            out.model = selectedKey
        }

        if (typeof payload?.selectedAspectRatio === 'string' && payload.selectedAspectRatio) {
            out.aspect_ratio = payload.selectedAspectRatio
        }
        if (typeof payload?.selectedResolution === 'string' && payload.selectedResolution) {
            out.resolution = payload.selectedResolution
        }
        const rawDuration = payload?.selectedDuration
        if (typeof rawDuration === 'number' && Number.isFinite(rawDuration) && rawDuration > 0) {
            out.duration = rawDuration
        }
        const rawCount = payload?.selectedOutputCount
        const n =
            typeof rawCount === 'number'
                ? rawCount
                : typeof rawCount === 'string'
                    ? Number(rawCount)
                    : NaN
        if (Number.isFinite(n) && n > 0) {
            out.output_number = n
        }
        if (typeof payload?.generate_audio === 'boolean') {
            out.generate_audio = payload.generate_audio
        }
        if (typeof payload?.public_visible === 'boolean') {
            out.public_visible = payload.public_visible
        }
        if (typeof payload?.copyright_protection === 'boolean') {
            out.copyright_protection = payload.copyright_protection
        }
        if (typeof payload?.negative_prompt === 'string' && payload.negative_prompt.trim()) {
            out.negative_prompt = payload.negative_prompt
        }

        // Workbench character-to-video inputs
        const mode =
            typeof payload?.character_to_video_mode_select === 'string'
                ? payload.character_to_video_mode_select
                : ''
        const frameEnd =
            typeof payload?.character_to_video_frame_end_mode === 'boolean'
                ? payload.character_to_video_frame_end_mode
                : false

        // Characters:
        // - first/last mode: consumer expects [first, last] packed into one array
        // - multi/single: consumer expects a flat array
        const firstChars = Array.isArray(payload?.character_to_video_character_select_list)
            ? payload.character_to_video_character_select_list.filter(Boolean)
            : []
        const lastChars = Array.isArray(payload?.character_to_video_last_character_select_list)
            ? payload.character_to_video_last_character_select_list.filter(Boolean)
            : []

        // Image urls
        const multiImages = Array.isArray(payload?.character_to_video_image_urls)
            ? payload.character_to_video_image_urls.filter((it: any) => typeof it === 'string' && it)
            : []
        const firstImageUrl =
            typeof payload?.character_to_video_first_image_url === 'string'
                ? payload.character_to_video_first_image_url
                : ''
        const lastImageUrl =
            typeof payload?.character_to_video_last_image_url === 'string'
                ? payload.character_to_video_last_image_url
                : ''

        if (mode === 'multi') {
            out.image_type = 'multiple_frames'
            out.frame_end_mode = false
            out.image_list = multiImages
            // tool page does not rely on image_url for multiple frames; keep it empty
            out.image_url = ''
            out.character_select_list = firstChars
        } else {
            out.image_type = 'single_frame'
            out.frame_end_mode = frameEnd
            if (frameEnd) {
                const resolvedFirst = firstImageUrl || multiImages[0] || ''
                out.first_image_url = resolvedFirst
                out.last_image_url = lastImageUrl
                out.image_url = resolvedFirst
                // pack first/last character into array indices [0]/[1] (if present)
                const packed: any[] = []
                if (firstChars[0]) packed.push(firstChars[0])
                if (lastChars[0]) packed.push(lastChars[0])
                out.character_select_list = packed
            } else {
                const resolvedUrl = firstImageUrl || multiImages[0] || ''
                out.image_url = resolvedUrl
                out.first_image_url = resolvedUrl
                out.last_image_url = ''
                out.character_select_list = firstChars
            }
        }

        return out
    }
    if (secondaryId === 'IMAGE_TO_VIDEO') {
        return buildWorkbenchImageToVideoPrefillFromPayload(payload)
    }
    if (secondaryId === 'KEYFRAMES') {
        // Keep the payload shape consistent (same as IMAGE_TO_VIDEO) and let the target page
        // interpret `intent==='KEYFRAMES'` as "first+last frame".
        const out = buildWorkbenchImageToVideoPrefillFromPayload(payload)
        // semantic hint: KEYFRAMES should be handled as single_frame on the target page
        out.image_type = 'single_frame'
        return out
    }
    if (secondaryId === 'VOICE' || secondaryId === 'PURE_MUSIC') {
        const out: WorkbenchTextToMusicPrefill = {}

        // Mode mapping by secondary id
        out.mode = secondaryId === 'VOICE' ? 'vocal' : 'inst'

        const gs = payload?.groupSelections
        const musicInput =
            typeof gs?.music_inputText === 'string' ? gs.music_inputText : ''
        if (musicInput) {
            out.inputText = musicInput
            // keep description in sync; Tool page submits `description` to API
            out.description = musicInput
        }

        // Tags: Workbench stores `voice_tags` as Record<string, string> (value -> label)
        if (gs?.voice_tags && typeof gs.voice_tags === 'object' && !Array.isArray(gs.voice_tags)) {
            out.styles = gs.voice_tags as Record<string, string>
        } else {
            out.styles = {}
        }

        // Lyrics only for vocal (VOICE)
        if (secondaryId === 'VOICE') {
            if (typeof gs?.music_lyrics === 'string') out.lyrics = gs.music_lyrics
        }

        // Reference audio
        if (typeof gs?.reference_audio === 'string') {
            out.referenceAudioUrl = gs.reference_audio
        }
        if (typeof gs?.audio_name === 'string') {
            out.referenceAudioName = gs.audio_name
        }

        // Gender mapping (VOICE only)
        if (secondaryId === 'VOICE') {
            const rawGender = typeof gs?.voice_gender === 'string' ? gs.voice_gender : ''
            if (rawGender === 'BOY_VOICE') out.gender = 'm'
            else if (rawGender === 'GIRL_VOICE') out.gender = 'f'
        }

        if (typeof payload?.public_visible === 'boolean') {
            out.public_visible = payload.public_visible
        }
        if (typeof payload?.copyright_protection === 'boolean') {
            out.copyright_protection = payload.copyright_protection
        }

        return out
    }
    return null
}

export function getWorkbenchRouteTargetBySecondary(
    secondaryId: SecondaryTabId | null | undefined
): WorkbenchRouteTarget | null {
    if (!secondaryId) return null
    return WORKBENCH_ROUTE_BY_SECONDARY[secondaryId] || null
}
